import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import NorthIcon from '@mui/icons-material/North';
import PeopleIcon from '@mui/icons-material/People';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SouthIcon from '@mui/icons-material/South';
import StarRateIcon from '@mui/icons-material/StarRate';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Badge, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, IconButton, IconButtonProps, Portal, Radio, RadioGroup, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, keyframes, styled, useTheme } from '@mui/material/styles';
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AttachmentTaskType from "../../enum/AttachmentTaskType";
import Audit from "../../model/Audit";
import CommentDocumentVersion from "../../model/CommentDocumentVersion";
import CommentOcurrence from "../../model/CommentOcurrence";
import CommentTask from "../../model/CommentTask";
import CommentTaskUserEmote from "../../model/CommentTaskUserEmote";
import CommentTechnicalAssistance from "../../model/CommentTechnicalAssistance";
import User from "../../model/User";
import { useAuthentication } from "../../reducer/authentication";
import { ArrayCondition, WhereCondition } from "../../service/ServiceApi";
import { ServiceApi } from "../../service/ServiceApiWithLoading";
import TaskApi from "../../service/TaskApi";
import { isAdminOrSuperAdmin, objectClone, sortObjectArrayByProp } from "../../utils";
import { Listener } from '../../utils/EventEmitter';
import AttachmentPreviewBox from "../AttachmentPreviewBox";
import AuditLabelValue from "../AuditLabel/AuditLabelValue";
import useAuditLabel from "../AuditLabel/useAuditLabel";
import confirmation from "../ConfirmationDialog";
import eventEmitter from "../SafeDeskEventProvider";
import TableListPsim from "../TableListPsim/TableListPsim";
import TicketStatusIconAsync from "../TicketStatusIconAsync";
import UserPhoto from "../UserPhoto";
import UserAdminType from '../../enum/UserAdminType';
import { useLanzacoinShop } from '../../context/LanzacoinShopContext';
import DocumentVersionStatus from '../../enum/DocumentVersionStatus';
import CommentTaskApi from '../../service/CommentTaskApi';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Task from '../../model/Task';
import FixedIds from '../../enum/FixedIds';
import TaskSendEmailType from '../../enum/TaskSendEmailType';
import UserType from '../../enum/UserType';

const apiAudit = new ServiceApi<Audit>('audit');

type EmoteKey = 'emote1' | 'emote2' | 'emote3' | 'emote4';

const EMOTE_KEY_BY_ID: Record<number, EmoteKey> = {
    1: 'emote1',
    2: 'emote2',
    3: 'emote3',
    4: 'emote4',
};

const EMOTE_ANIMATION_DURATION = 2200;
const DISLIKE_ANIMATION_DURATION = 10000;

/* ── Dislike: like → flip → red → explode ── */
const dislikeFlip = keyframes`
    0%   { transform: scale(1)   rotateX(0deg);   color: #66bb6a; filter: drop-shadow(0 0 18px rgba(102,187,106,0.8)); opacity: 1; }
    8%   { transform: scale(1.1) rotateX(0deg);   color: #66bb6a; filter: drop-shadow(0 0 22px rgba(102,187,106,0.9)); }
    20%  { transform: scale(1)   rotateX(60deg);  color: #a5d6a7; filter: drop-shadow(0 0 16px rgba(102,187,106,0.6)); }
    35%  { transform: scale(1.05) rotateX(120deg); color: #ffb74d; filter: drop-shadow(0 0 18px rgba(255,183,77,0.7)); }
    50%  { transform: scale(1.1) rotateX(180deg); color: #ef5350; filter: drop-shadow(0 0 22px rgba(239,83,80,0.8)); }
    58%  { transform: scale(1.2) rotateX(180deg); color: #f44336; filter: drop-shadow(0 0 28px rgba(244,67,54,0.9)); }
    64%  { transform: scale(1.15) rotateX(180deg); color: #f44336; filter: drop-shadow(0 0 24px rgba(244,67,54,0.85)); }
    72%  { transform: scale(1.25) rotateX(180deg); color: #d32f2f; filter: drop-shadow(0 0 32px rgba(211,47,47,0.95)); }
    80%  { transform: scale(1.8) rotateX(180deg); color: #d32f2f; filter: drop-shadow(0 0 50px rgba(211,47,47,1)); }
    88%  { transform: scale(2.8) rotateX(180deg); color: #b71c1c; filter: drop-shadow(0 0 80px rgba(183,28,28,1)); opacity: 1; }
    100% { transform: scale(4)   rotateX(180deg); color: #b71c1c; filter: drop-shadow(0 0 120px rgba(183,28,28,0)); opacity: 0; }
`;

const dislikeShockwave = keyframes`
    0%   { transform: scale(0); opacity: 0; }
    72%  { transform: scale(0); opacity: 0; }
    80%  { transform: scale(0.5); opacity: 0.7; }
    90%  { transform: scale(2.5); opacity: 0.3; }
    100% { transform: scale(4); opacity: 0; }
`;

const dislikeParticle = keyframes`
    0%   { transform: translate(-50%,-50%) scale(0); opacity: 0; }
    75%  { transform: translate(-50%,-50%) scale(0); opacity: 0; }
    82%  { transform: translate(-50%,-50%) scale(1.2); opacity: 1; }
    90%  { transform: translate(-50%,-50%) scale(0.8); opacity: 0.7; }
    100% { transform: translate(-50%,-50%) scale(0); opacity: 0; }
`;

const overlayFade = keyframes`
    0% { opacity: 0; }
    10% { opacity: 1; }
    85% { opacity: 1; }
    100% { opacity: 0; }
`;

const rocketLaunch = keyframes`
    0% { transform: translate(-50%, 40%) scale(0.6); opacity: 0; }
    20% { opacity: 1; }
    60% { transform: translate(-50%, -30%) scale(1); opacity: 1; }
    100% { transform: translate(-50%, -140%) scale(0.85); opacity: 0; }
`;

const rocketTrail = keyframes`
    0% { opacity: 0; height: 0; }
    20% { opacity: 0.9; height: 3rem; }
    60% { opacity: 0.6; height: 6rem; }
    100% { opacity: 0; height: 0; }
`;

const iconPop = keyframes`
    0% { transform: scale(0.3); opacity: 0; }
    35% { transform: scale(1.2); opacity: 1; }
    60% { transform: scale(0.95); opacity: 1; }
    100% { transform: scale(0.7); opacity: 0; }
`;

const textWave = keyframes`
    0% { letter-spacing: 24px; opacity: 0; transform: translateY(25px); }
    35% { letter-spacing: 8px; opacity: 1; transform: translateY(0); }
    75% { opacity: 1; }
    100% { opacity: 0; transform: translateY(-25px); }
`;

const shimmer = keyframes`
    0% { transform: scale(0.4); opacity: 0; }
    35% { transform: scale(1); opacity: 0.9; }
    70% { transform: scale(1.2); opacity: 0.6; }
    100% { transform: scale(1.4); opacity: 0; }
`;

const profileGlow = keyframes`
    0% { box-shadow: 0 0 4px 1px rgba(255, 215, 0, 0.4), 0 0 8px 2px rgba(255, 180, 0, 0.2); }
    50% { box-shadow: 0 0 8px 3px rgba(255, 215, 0, 0.7), 0 0 16px 6px rgba(255, 180, 0, 0.35); }
    100% { box-shadow: 0 0 4px 1px rgba(255, 215, 0, 0.4), 0 0 8px 2px rgba(255, 180, 0, 0.2); }
`;

const EmoteOverlay = styled('div', {
    shouldForwardProp: (prop) => prop !== 'animDuration',
})<{ animDuration?: number }>(({ theme, animDuration }) => ({
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal + 2,
    background: 'rgba(10, 16, 28, 0.35)',
    backdropFilter: 'blur(2px)',
    animation: `${overlayFade} ${animDuration ?? EMOTE_ANIMATION_DURATION}ms ease forwards`,
}));

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

interface AuditItemProps {
    audit: Audit;
    noUserPhoto?: boolean;
    getLineTitle?: (audit: Audit) => React.ReactNode;
    user?: User | null;
}

interface AuditDataRow {
    id: number;
    label: React.ReactNode;
    dataBefore: React.ReactNode;
    dataAfter: React.ReactNode;
}

function AuditItem({ audit, noUserPhoto, getLineTitle, user }: AuditItemProps) {
    const { highlightedUserIds } = useLanzacoinShop();
    const { action, controller } = audit;

    let data = audit.data ? JSON.parse(audit.data) : null;
    const al = useAuditLabel();
    let actionName: React.ReactNode = getActionName();
    let postsAndDeletes = ['post', 'saveAttachment', 'saveChecklist', 'delete', 'deleteAttachment', 'deleteChecklist'];
    let isRegisterOrDelete = postsAndDeletes.includes(action);
    let isUpdateDelete = ['deleteFile'].includes(action);
    let iconBackgroundColor = "#045a8e";
    let icon: React.ReactNode = <EditIcon style={{ color: 'white', fontSize: 14 }} />;
    const [expanded, setExpanded] = useState<boolean>(false);

    const objectKeys = data ? Object.keys(data) : [];

    const isOnlyNameChange = action === 'put' && objectKeys.length === 1 ? objectKeys.find((fieldName) => fieldName === 'name') : null;
    const isProductionStatusChange = action === 'put' && data && controller === 'production' ? objectKeys.find((fieldName) => fieldName === 'status') : null;
    const isDocumentVersionStatusChange = action === 'put' && data && controller === 'documentVersion' ? objectKeys.find((fieldName) => fieldName === 'status') : null;
    const isStatusChange = action !== 'post' && data ? objectKeys.find((fieldName) => fieldName === 'ticket_status_id') : null;
    const isUserChange = action !== 'post' && data ? objectKeys.find((fieldName) => fieldName === 'user_id') : null;
    const isPriorityChange = action !== 'post' && data ? objectKeys.find((fieldName) => fieldName === 'priority') : null;
    const isTagChange = action === 'updateTags' ? true : false;
    const isDelete = action === 'delete' ? true : false;
    if (isDelete) {
        iconBackgroundColor = 'red';
        icon = <DeleteIcon style={{ color: 'white', fontSize: 14 }} />;
        // actionName = <>Registro foi excluído</>;
    } else if (isOnlyNameChange) {
        actionName = <>Assunto foi alterado de "<AuditLabelValue audit={audit} fieldName={'name'} value={data['name']['before']} />"
            para "<AuditLabelValue audit={audit} fieldName={'name'} value={data['name']['after']} />"</>;
    } else if (isTagChange) {
        actionName = <>Atualizou as tags para: <AuditLabelValue audit={audit} fieldName={'tags'} value={data['tags']['after']} /></>;
    } else if (isProductionStatusChange) {
        iconBackgroundColor = '#17bb00';
        icon = <SyncAltIcon style={{ color: 'white', fontSize: 14 }} />;
        actionName = <>Alteração de Status de "{data['status']['before'] === 1 ? 'Concluído' : 'Aberto'}"
            para "{data['status']['after'] === 1 ? 'Concluído' : 'Aberto'}"</>;
    } else if (isDocumentVersionStatusChange) {
        iconBackgroundColor = '#17bb00';
        icon = <SyncAltIcon style={{ color: 'white', fontSize: 14 }} />;
        actionName = <>Alteração de Status de "{data['status']['before'] === DocumentVersionStatus.Rejected ? 'Reprovado' : data['status']['before'] === DocumentVersionStatus.Approved ? 'Aprovado' : data['status']['before'] === DocumentVersionStatus.AwaitingApproval ? 'Aguardando aprovação' : 'Em andamento'}"
            para "{data['status']['after'] === DocumentVersionStatus.Rejected ? 'Reprovado' : data['status']['after'] === DocumentVersionStatus.Approved ? 'Aprovado' : data['status']['after'] === DocumentVersionStatus.AwaitingApproval ? 'Aguardando aprovação' : 'Em andamento'}"</>;
    } else if (isStatusChange) {
        icon = <TicketStatusIconAsync ticket_status_id={data['ticket_status_id']['after']} style={{ color: 'white', fontSize: 14 }} />;//<TicketStatusIcon icon={row.ticket_status.icon} style={{ marginRight: 5 }} />;
        if (isUserChange) {
            actionName = <>Alteração de Status de "<AuditLabelValue audit={audit} fieldName={'ticket_status_id'} value={data['ticket_status_id']['before']} />"
                para "<AuditLabelValue audit={audit} fieldName={'ticket_status_id'} value={data['ticket_status_id']['after']} />"
                e transferido de "<AuditLabelValue audit={audit} fieldName={'user_id'} value={data['user_id']['before']} />"
                para "<AuditLabelValue audit={audit} fieldName={'user_id'} value={data['user_id']['after']} />"</>;
        } else {
            actionName = <>Alteração de Status de "<AuditLabelValue audit={audit} fieldName={'ticket_status_id'} value={data['ticket_status_id']['before']} />"
                para "<AuditLabelValue audit={audit} fieldName={'ticket_status_id'} value={data['ticket_status_id']['after']} />"</>;
        }
    } else if (isUserChange) {
        iconBackgroundColor = '#17bb00';
        icon = <SyncAltIcon style={{ color: 'white', fontSize: 14 }} />;
        actionName = <>Transferido de "<AuditLabelValue audit={audit} fieldName={'user_id'} value={data['user_id']['before']} />"
            para "<AuditLabelValue audit={audit} fieldName={'user_id'} value={data['user_id']['after']} />"</>;
    } else if (isPriorityChange) {
        iconBackgroundColor = '#045a8e';
        let priorityBefore = data['priority']['before'] ? parseInt(data['priority']['before']) : 0;
        let priorityAfter = data['priority']['after'] ? parseInt(data['priority']['after']) : 0;
        icon = priorityBefore > priorityAfter ? <SouthIcon style={{ color: 'white', fontSize: 14 }} /> : <NorthIcon style={{ color: 'white', fontSize: 14 }} />;
        actionName = <>Alteração de Prioridade de "<AuditLabelValue audit={audit} fieldName={'priority'} value={data['priority']['before']} />"
            para "<AuditLabelValue audit={audit} fieldName={'priority'} value={data['priority']['after']} />"</>;
    } else if (['saveAttachment', 'deleteAttachment', 'updateAttachment', 'updateAttachmentObservation'].includes(action)) {
        icon = <AttachFileIcon style={{ color: 'white', fontSize: 14 }} />;
        iconBackgroundColor = 'rgb(136, 136, 136)';
    } else if (action === 'updateCustomerContacts') {
        icon = <PeopleIcon style={{ color: 'white', fontSize: 14 }} />;
        iconBackgroundColor = 'rgb(136, 136, 136)';
    } else if (action === 'updateObservers') {
        icon = <VisibilityIcon style={{ color: 'white', fontSize: 14 }} />;
        iconBackgroundColor = 'rgb(136, 136, 136)';
    } else if (action === 'updateUsers') {
        icon = <PeopleIcon style={{ color: 'white', fontSize: 14 }} />;
        iconBackgroundColor = 'rgb(136, 136, 136)';
    } else if (action === 'put' && controller === 'task') {
        actionName = <>{actionName} ({getDataLabels(data)})</>
    }

    function getDataLabels(data: any) {
        return Object.keys(data).filter(filterAuditData).map((fieldName, i) => (
            al(controller, action, fieldName)
        )).join(', ');
    }

    function filterAuditData(fieldName: string) {
        if (action === 'saveAttachment' || action === 'deleteAttachment' || action === 'updateAttachment') {
            if (fieldName === 'comment_task_id') {
                return false;
            }
        } else if (controller === 'documentVersion') {
            if (fieldName === 'link_drive' && user?.admin_type !== UserAdminType.SuperAdmin) {
                return false;
            }
        } else if(controller === 'task'){
            if(fieldName === 'requires_email_approval'){
                return false;
            }
        }
        return true;
    }

    function getDataRow(data: any): AuditDataRow[] {
        return Object.keys(data).filter(filterAuditData).map((fieldName, i) => (
            {
                id: i,
                label: al(controller, action, fieldName),
                dataBefore: <AuditLabelValue audit={audit} fieldName={fieldName} value={data[fieldName]['before']} />,
                dataAfter: <AuditLabelValue audit={audit} fieldName={fieldName} value={data[fieldName]['after']} />
            }
        ))
    }

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    function getActionName() {
        let treatedName = al(controller, action, 'action_name');
        if (['saveAttachment', 'deleteAttachment', 'updateAttachment', 'updateAttachmentObservation'].includes(action)) {
            if (controller === 'task') {
                treatedName += ' - ' + (audit.attachment_task?.name || audit.attachment_task?.system_attachment?.file || '');
            } else if (controller === 'technicalAssistance') {
                treatedName += ' - ' + audit.attachment_technical_assistance?.name;
            }
        }
        return treatedName;
    }

    function getTitle() {
        if (getLineTitle) {
            return getLineTitle(audit);
        }
        return audit.audit_user?.name || '';
    }

    if (!audit.audit_user) return null;
    const isHighlighted = user?.use_lanza_coin && highlightedUserIds.has(audit.audit_user.id);
    return (
        <Box marginBottom={3} display="flex">
            <Box alignItems={"center"} marginRight={1} style={{ backgroundColor: iconBackgroundColor, padding: "0 2px", width: 14 }} display="flex">
                {icon}
            </Box>
            <Box display="flex" flex={1} flexDirection="column">
                <Box display="flex">
                    {!noUserPhoto && <Box height={60} sx={isHighlighted ? {
                        border: '2px solid #FFD700',
                        borderRadius: '6px',
                        animation: `${profileGlow} 2s ease-in-out infinite`,
                    } : undefined}>
                        <UserPhoto key={`userphoto-${audit.audit_user.id}`} style={{ objectFit: 'contain' }} id={audit.audit_user.id} photo={audit.audit_user.photo} width={isHighlighted ? 51 : 55} height={isHighlighted ? 56 : 60} useLanzaCoin={audit.audit_user.use_lanza_coin} experience={audit.audit_user.experience} />
                    </Box>}
                    <Box flex={1} paddingLeft={1}>
                        <Box display="flex" borderBottom={'1px solid rgb(4, 90, 142)'}>
                            <Typography variant='subtitle2' style={{ fontWeight: '700', flex: 1 }}>{getTitle()}</Typography>
                            <Typography variant='caption' textAlign="right">{moment(audit.created_at).format('DD/MM/YYYY HH:mm:ss')}</Typography>
                        </Box>
                        <Box display="flex">
                            <Typography variant='caption' style={{ flex: 1 }}>{actionName}</Typography>
                        </Box>
                        {audit.attachment_task && !audit.attachment_task.deleted_at ? <Box display={'flex'}>
                            <AttachmentPreviewBox key={`attachment-${audit.attachment_task.id || audit.id}`} fullImageWidth id={audit.attachment_task.task_id || null} model="Task" name={audit.attachment_task.name} systemAttachmentId={audit.attachment_task.system_attachment_id} systemAttachmentFile={audit.attachment_task.system_attachment?.file} />
                        </Box> : null}
                    </Box>
                    <Box sx={{ overflow: 'hidden' }}>
                        <ExpandMore
                            expand={expanded}
                            onClick={handleExpandClick}
                            aria-expanded={expanded}
                            aria-label="show more"
                            size='small'
                            color="inherit"
                        >
                            <ExpandMoreIcon />
                        </ExpandMore>
                    </Box>
                </Box>
                <Collapse in={expanded} unmountOnExit={true} timeout="auto">
                    <Box marginTop={1}>
                        {data ? <TableListPsim<AuditDataRow>
                            columns={[
                                { field: 'id', visible: false, value: 'id' },
                                { field: 'label', value: 'Campo', width: 150 },
                                { field: 'dataBefore', value: isUpdateDelete ? 'Valor' : 'Antes', visible: !isRegisterOrDelete },
                                { field: 'dataAfter', value: isRegisterOrDelete ? 'Valor' : 'Depois', visible: !isUpdateDelete },
                            ]}
                            rows={getDataRow(data)}
                            noPadding
                        /> : null}
                    </Box>
                </Collapse>
            </Box>
        </Box>
    )
}

interface CommentItemProps {
    idx: number;
    comment: CommentTask | CommentTechnicalAssistance | CommentOcurrence | CommentDocumentVersion;
    updateCommentResolved: (commentTaskUserId: number) => Promise<void>;
    updateCommentAwared: (commentTaskUserId: number) => Promise<void>;
    updateCommentCanceled: (commentTaskId: number) => Promise<void>;
    noUserPhoto?: boolean;
    disableCommentActions?: boolean;
    getLineTitle?: (comment: CommentTask | CommentTechnicalAssistance) => React.ReactNode;
    showAssignedUsers?: boolean;
    addEmote: (commentId: number, emoteId: number) => Promise<boolean>;
    showGeneratedTaskCreation?: boolean;
    createTaskFromComment?: (comment: CommentTask, assignedUserId: number) => Promise<void>;
    task?: Task;
}

type CreateTaskRecipientOption = 'PEDRO' | 'SELF' | 'TASK_OWNER' | 'COMMENT_OWNER';

interface CreateTaskRecipientDefinition {
    value: CreateTaskRecipientOption;
    label: string;
    userId: number | null;
    disabled: boolean;
    disabledReason?: string;
}

interface CreateTaskRecipientMeta {
    helper: string;
    chipLabel?: string | null;
    chipColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

export function CommentItem({ comment, updateCommentResolved, updateCommentCanceled, updateCommentAwared, idx, noUserPhoto, disableCommentActions, getLineTitle,
    showAssignedUsers, addEmote, showGeneratedTaskCreation, createTaskFromComment, task }: CommentItemProps) {
    const { user } = useAuthentication();
    const { highlightedUserIds, availableDislikes, emoteLimits, refresh: refreshShopContext } = useLanzacoinShop();
    const [expanded, setExpanded] = useState<boolean>(true);
    const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState<boolean>(false);
    const [selectedCreateTaskRecipient, setSelectedCreateTaskRecipient] = useState<CreateTaskRecipientOption | null>(null);
    const [createTaskDialogLoading, setCreateTaskDialogLoading] = useState<boolean>(false);
    const theme = useTheme();

    const commentEmotes = useMemo<CommentTaskUserEmote[]>(() => {
        if ('emotes' in comment && comment.emotes) {
            return comment.emotes;
        }
        return [];
    }, [comment]);

    const emoteDefinitions = useMemo(() => ([
        { id: 1, key: 'emote1' as EmoteKey, Icon: ThumbUpIcon, label: 'Gostei', color: theme.palette.success.main, requiresPurchase: false },
        { id: 2, key: 'emote2' as EmoteKey, Icon: RocketLaunchIcon, label: 'Foguete', color: theme.palette.info.main, requiresPurchase: false },
        { id: 3, key: 'emote3' as EmoteKey, Icon: StarRateIcon, label: 'Estrela', color: theme.palette.warning.main, requiresPurchase: false },
        { id: 4, key: 'emote4' as EmoteKey, Icon: ThumbDownIcon, label: 'Dislike', color: theme.palette.error.main, requiresPurchase: true },
    ]), [theme]);

    const createTaskRecipientOptions = useMemo<CreateTaskRecipientDefinition[]>(() => ([
        {
            value: 'PEDRO',
            label: 'Criar demanda a partir deste comentário para o Pedro',
            userId: FixedIds.idPedro,
            disabled: false
        },
        {
            value: 'SELF',
            label: `Criar demanda a partir deste comentário para mim mesmo (${user?.name})`,
            userId: user?.id ?? null,
            disabled: !(user?.id && !!user.active && user.type === UserType.Commbox && !!user.perm_task),
        },
        {
            value: 'TASK_OWNER',
            label: `Criar demanda a partir deste comentário para o atual dono deste chamado (${task?.user?.name})`,
            userId: task?.user_id ?? null,
            disabled: !(task?.user_id && !!task.user && !!task.user.active && task.user.type === UserType.Commbox && !!task.user.perm_task)
        },
        {
            value: 'COMMENT_OWNER',
            label: `Criar demanda a partir deste comentário para o dono deste comentário (${comment.user?.name})`,
            userId: comment.user?.id ?? null,
            disabled: !(comment.user?.id && !!comment.user.active && comment.user.type === UserType.Commbox && !!comment.user.perm_task)
        }
    ]), [comment.user?.id, comment.user?.name, task?.user_id, task?.user?.name, user?.id, user?.name]);

    const selectedRecipientDefinition = useMemo(() => (
        createTaskRecipientOptions.find(option => option.value === selectedCreateTaskRecipient) || null
    ), [createTaskRecipientOptions, selectedCreateTaskRecipient]);

    const isConfirmCreateTaskDisabled = createTaskDialogLoading || !selectedRecipientDefinition || !selectedRecipientDefinition.userId;

    const canGenerateTaskFromComment = Boolean(
        showGeneratedTaskCreation &&
        comment.id &&
        user?.perm_add_task_internal &&
        user &&
        'generated_task_id' in comment &&
        !comment.generated_task_id
    );

    const handleOpenCreateTaskDialog = useCallback(() => {
        setSelectedCreateTaskRecipient(null);
        setCreateTaskDialogOpen(true);
    }, []);

    const handleCloseCreateTaskDialog = useCallback(() => {
        if (createTaskDialogLoading) return;
        setCreateTaskDialogOpen(false);
        setSelectedCreateTaskRecipient(null);
    }, [createTaskDialogLoading]);

    const handleConfirmCreateTaskDialog = useCallback(async () => {
        if (!createTaskFromComment || !selectedRecipientDefinition?.userId || !('generated_task_id' in comment)) {
            return;
        }
        try {
            setCreateTaskDialogLoading(true);
            await createTaskFromComment(comment as CommentTask, selectedRecipientDefinition.userId);
            setCreateTaskDialogOpen(false);
            setSelectedCreateTaskRecipient(null);
        } finally {
            setCreateTaskDialogLoading(false);
        }
    }, [comment, createTaskFromComment, selectedRecipientDefinition]);

    const commentPreview = useMemo(() => {
        const raw = (comment as any)?.comment ?? '';
        const textOnly = raw
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (!textOnly) return '';
        return textOnly.length > 220 ? `${textOnly.substring(0, 220)}…` : textOnly;
    }, [comment]);

    const taskReferenceLabel = useMemo(() => {
        if (task?.ticket_code) return `#${task.ticket_code}`;
        if (task?.id) return `ID ${task.id}`;
        return 'este chamado';
    }, [task?.id, task?.ticket_code]);

    const optionMeta = useMemo<Record<CreateTaskRecipientOption, CreateTaskRecipientMeta>>(() => ({
        PEDRO: {
            helper: 'Encaminha para o responsável padrão definido pelo time interno.',
            chipLabel: 'Pedro',
            chipColor: 'info'
        },
        SELF: {
            helper: 'Você assume a nova demanda imediatamente e será notificado.',
            chipLabel: user?.name || null,
            chipColor: 'primary'
        },
        TASK_OWNER: {
            helper: task?.user?.name ? `${task.user.name} continuará responsável pelo novo fluxo.` : 'Mantém o responsável atual do chamado.',
            chipLabel: task?.user?.name || null,
            chipColor: 'success'
        },
        COMMENT_OWNER: {
            helper: comment.user?.name ? `${comment.user.name} receberá a nova demanda.` : 'Entrega para quem registrou o comentário.',
            chipLabel: comment.user?.name || null,
            chipColor: 'warning'
        }
    }), [comment.user?.name, task?.user?.name, user?.name]);

    const currentUserId = user?.id ?? null;
    const canReact = Boolean(comment.id && currentUserId && !disableCommentActions);
    const [emoteLoadingId, setEmoteLoadingId] = useState<number | null>(null);
    const [activeEmoteAnimation, setActiveEmoteAnimation] = useState<number | null>(null);
    const isReactDisabled = !canReact || emoteLoadingId !== null;

    useEffect(() => {
        if (activeEmoteAnimation === null) {
            return;
        }
        const duration = activeEmoteAnimation === 4 ? DISLIKE_ANIMATION_DURATION : EMOTE_ANIMATION_DURATION;
        const timeout = window.setTimeout(() => setActiveEmoteAnimation(null), duration);
        return () => window.clearTimeout(timeout);
    }, [activeEmoteAnimation]);

    const handleEmoteClick = useCallback(async (emoteId: number) => {
        if (!canReact || currentUserId === null || !comment.id || emoteLoadingId !== null) {
            return;
        }

        if (!EMOTE_KEY_BY_ID[emoteId]) {
            return;
        }

        setEmoteLoadingId(emoteId);
        try {
            const success = await addEmote(comment.id, emoteId);
            if (success) {
                const definition = emoteDefinitions.find(item => item.id === emoteId);
                if (definition) {
                    // trigger celebration animation after successful reaction
                    setActiveEmoteAnimation(emoteId);
                }
                // Refresh context so remaining counts and dislike availability update
                refreshShopContext();
            }
        } finally {
            setEmoteLoadingId(null);
        }
    }, [addEmote, canReact, comment.id, currentUserId, emoteDefinitions, emoteLoadingId, refreshShopContext]);

    const renderEmoteAnimation = useCallback((emoteId: number) => {
        const definition = emoteDefinitions.find(item => item.id === emoteId);
        const label = definition ? definition.label : '';

        if (emoteId === 2) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Box sx={{ position: 'relative', width: '10rem', height: '12rem' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '14%',
                                left: '50%',
                                width: '1.6rem',
                                height: '6rem',
                                background: 'linear-gradient(180deg, rgba(129,212,250,0.95) 0%, rgba(21,101,192,0.05) 100%)',
                                transform: 'translateX(-50%)',
                                borderRadius: '999px',
                                filter: 'blur(10px)',
                                animation: `${rocketTrail} ${EMOTE_ANIMATION_DURATION}ms ease-out forwards`,
                            }}
                        />
                        <RocketLaunchIcon
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                transform: 'translate(-50%, 0)',
                                fontSize: { xs: '8rem', sm: '9rem', md: '10rem' },
                                color: theme.palette.info.light,
                                filter: 'drop-shadow(0 0 26px rgba(100,181,246,0.8))',
                                animation: `${rocketLaunch} ${EMOTE_ANIMATION_DURATION}ms cubic-bezier(0.2,0.6,0.2,1) forwards`,
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        if (emoteId === 3) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Box sx={{ position: 'relative', width: '12rem', height: '12rem' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,213,79,0.6) 50%, rgba(255,213,79,0) 75%)',
                                animation: `${shimmer} ${EMOTE_ANIMATION_DURATION}ms ease-in-out forwards`,
                            }}
                        />
                        <StarRateIcon
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                margin: 'auto',
                                fontSize: { xs: '8rem', md: '9rem' },
                                color: theme.palette.warning.light,
                                filter: 'drop-shadow(0 0 24px rgba(255,215,64,0.8))',
                                animation: `${iconPop} ${EMOTE_ANIMATION_DURATION}ms ease-out forwards`,
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        if (emoteId === 4) {
            const D = DISLIKE_ANIMATION_DURATION;
            const particles = Array.from({ length: 10 }).map((_, i) => {
                const angle = (i / 10) * 360;
                const rad = (angle * Math.PI) / 180;
                const dist = 80 + Math.random() * 40;
                const tx = Math.cos(rad) * dist;
                const ty = Math.sin(rad) * dist;
                return (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            top: `calc(50% + ${ty}px)`,
                            left: `calc(50% + ${tx}px)`,
                            width: i % 3 === 0 ? '12px' : '8px',
                            height: i % 3 === 0 ? '12px' : '8px',
                            borderRadius: '50%',
                            backgroundColor: i % 2 === 0 ? '#f44336' : '#ff7043',
                            animation: `${dislikeParticle} ${D}ms ease-out forwards`,
                        }}
                    />
                );
            });

            return (
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Box sx={{ position: 'relative', width: '14rem', height: '14rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {/* Shockwave ring */}
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                border: '3px solid rgba(244,67,54,0.6)',
                                animation: `${dislikeShockwave} ${D}ms ease-out forwards`,
                            }}
                        />
                        {/* Particles */}
                        {particles}
                        {/* Icon: ThumbUp that flips to ThumbDown via rotateX(180) and turns red */}
                        <ThumbUpIcon
                            sx={{
                                fontSize: { xs: '8rem', md: '9rem' },
                                animation: `${dislikeFlip} ${D}ms ease-in-out forwards`,
                            }}
                        />
                    </Box>
                </Box>
            );
        }

        return (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <ThumbUpIcon
                    sx={{
                        fontSize: { xs: '7rem', md: '8rem' },
                        color: theme.palette.success.light,
                        filter: 'drop-shadow(0 0 18px rgba(102,187,106,0.8))',
                        animation: `${iconPop} ${EMOTE_ANIMATION_DURATION}ms ease-out forwards`,
                    }}
                />
            </Box>
        );
    }, [emoteDefinitions, theme]);

    const commentUser = comment.user ? comment.user : (user ? user : null);
    const isAdmin = isAdminOrSuperAdmin(user);
    const isAttachmentFullWidth = 'task_id' in comment && comment.attachments && comment.attachments.length === 1;

    if (!commentUser) return null;

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const isCanceled = !!comment.canceled && comment.canceled_at;

    let created_at = comment.created_at ? moment(comment.created_at).format('DD/MM/YYYY HH:mm:ss') : '';

    function getRegisteredTime() {
        if (!commentUser || !('time_counter' in comment) || !comment.time_counter) return '';

        let hourString = String(comment.time_counter.hours).padStart(2, '0');
        let minuteString = String(comment.time_counter?.minutes).padStart(2, '0');
        return (isAdmin || commentUser.id === user?.id) ?
            <> - <MoreTimeIcon titleAccess="Tempo registrado" style={{ fontSize: 14, margin: '0 5px' }} />{`${hourString}:${minuteString}`}</> : '';
    }

    function getCommentSubtitle() {
        if (isCanceled) {
            return <React.Fragment key={`ci-1-${idx}`}>Comentário (cancelado: {moment(comment.canceled_at).format('DD/MM/YYYY HH:mm:ss')}) {getRegisteredTime()}</React.Fragment>;
        }

        let assignedUsers: React.ReactNode[] = [];
        if ('comment_task_users' in comment && comment.comment_task_users && comment.comment_task_users.length > 0 && showAssignedUsers) {
            comment.comment_task_users.forEach((ctu, idx2) => {
                let assignedUsersRow: React.ReactNode[] = [];
                assignedUsersRow.push(<React.Fragment key={`ci-2-${idx}-${idx2}`}><b style={{ textDecoration: 'underline' }}>{ctu.user?.name}</b>.</React.Fragment>);
                if (ctu.resolved_at) {
                    let awared_at = ctu.awared_at ? moment(ctu.awared_at).format('DD/MM/YYYY[, as] HH:mm') : '';
                    assignedUsersRow.push(<React.Fragment key={`ci-3-${idx}-${idx2}`}>&nbsp;Ciente no dia {awared_at}.</React.Fragment>);
                    let resolved_at = moment(ctu.resolved_at).format('DD/MM/YYYY[, as] HH:mm');
                    assignedUsersRow.push(<React.Fragment key={`ci-4-${idx}-${idx2}`}>&nbsp;Resolvido no dia {resolved_at}.</React.Fragment>);
                } else {
                    if (ctu.awared_at) {
                        let awared_at = moment(ctu.awared_at).format('DD/MM/YYYY[, as] HH:mm');
                        assignedUsersRow.push(<React.Fragment key={`ci-5-${idx}-${idx2}`}>&nbsp;Ciente no dia {awared_at}.</React.Fragment>);
                    } else {
                        if (ctu.user_id === user?.id && !disableCommentActions && comment.id) {
                            assignedUsersRow.push(<FormControlLabel
                                key={`ci-6-${idx}-${idx2}`}
                                style={{ margin: 0 }}
                                componentsProps={{ typography: { fontSize: '0.75rem' } }}
                                control={
                                    <Checkbox
                                        name='is_awared'
                                        onChange={async (_, v) => {
                                            await updateCommentAwared(ctu.id);
                                        }}
                                        checked={false}
                                        size="small"
                                        style={{ padding: '0 3px' }}
                                    />
                                }
                                label="Marcar como ciente"
                            />);
                        } else {
                            assignedUsersRow.push(<React.Fragment key={`ci-7-${idx}-${idx2}`}>&nbsp;Comentário não visualizado.</React.Fragment>);
                        }
                    }

                    if (ctu.user_id === user?.id && !disableCommentActions && comment.id) {
                        assignedUsersRow.push(<FormControlLabel
                            key={`ci-8-${idx}-${idx2}`}
                            style={{ margin: 0 }}
                            componentsProps={{ typography: { fontSize: '0.75rem' } }}
                            control={
                                <Checkbox
                                    name='is_resolved'
                                    onChange={async (_, v) => {
                                        await updateCommentResolved(ctu.id);
                                    }}
                                    checked={false}
                                    size="small"
                                    style={{ padding: '0 3px' }}
                                />
                            }
                            label="Marcar como resolvido"
                        />);
                    }
                }
                assignedUsers.push(<div key={`dv-assigned-user-${idx}-${idx2}`} style={{ display: 'flex', flexDirection: 'row' }}>{assignedUsersRow}</div>);
            });
        }
        let cancelButton = <></>;
        if (user?.id === comment.user_id && comment.id > 0 && comment.canceled !== undefined && !disableCommentActions) {
            cancelButton = <Tooltip key={`ci-9-${idx}`} placement="top" title={'Cancelar comentário'}>
                <IconButton
                    size='small'
                    color="inherit"
                    aria-label={'Cancelar comentário'}
                    onClickCapture={() => {
                        confirmation(undefined, {
                            title: "Cancelar o comentário?",
                            okLabel: 'Sim',
                            cancelLabel: 'Não',
                            onProceed: () => {
                                updateCommentCanceled(comment.id);
                            }
                        });
                    }}
                >
                    <DeleteIcon style={{ fontSize: 16, color: 'red' }} />
                </IconButton>
            </Tooltip>;
        }
        if (assignedUsers.length > 1) {
            return <div style={{ flex: 1, display: 'flex', flexDirection: 'column', marginBottom: 5 }}>
                <div style={{ flex: 1 }}>Comentário atribuído para:{cancelButton}{getRegisteredTime()}</div>
                {assignedUsers.map((au, idx2) => <div key={`dv-assigned-user-${idx}-${idx2}`} style={{ flex: 1, display: 'flex' }}>{au}</div>)}
            </div>
        } else if (assignedUsers.length === 1) {
            return <>Comentário atribuído para&nbsp;{assignedUsers} {cancelButton}{getRegisteredTime()}</>;
        } else {
            return <>Comentário{cancelButton}{getRegisteredTime()}</>;
        }
    }

    function sanatizeComment(comment: CommentTask | CommentTechnicalAssistance): string {
        const codeRegex = /<code>([\s\S]*?)<\/code>|(?:<div><br><\/div>)*(?:<div>)?&lt;code&gt;\s*(?:<\/div>)?([\s\S]*?)(?:<div>)?&lt;\/code&gt;(?:<\/div>)?/g;
        // Substituir trechos de código por placeholders únicos
        const commentWithPlaceholders = comment.comment.replace(
            codeRegex,
            (match, group1, group2) => {
                let content = (group1 || group2 || "").trim();
                if (moment(comment.created_at).isBefore(moment('2025-05-22'))) {
                    content = escapeHtml(content);
                }
                return `<div class="formatedCode">${content}</div>`;
            }
        );
        return commentWithPlaceholders;
    }

    // Função para escapar caracteres HTML
    function escapeHtml(str: string): string {
        return str
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    function getTitle() {
        if (getLineTitle) {
            return getLineTitle(comment);
        }

        const badges = emoteDefinitions.map(({ id, key, Icon, label, color, requiresPurchase }) => {
            const total = commentEmotes.reduce((acc, emote) => (emote[key] ? acc + 1 : acc), 0);
            const userHasUsed = currentUserId !== null && commentEmotes.some((emote) => emote.user_id === currentUserId && emote[key]);

            // Dislike (emote4): só mostra se tiver compras disponíveis OU alguma reação já existente
            if (requiresPurchase && total === 0 && availableDislikes <= 0 && !userHasUsed) {
                return null;
            }

            // Emote limit info from context
            const limitInfo = emoteLimits.find(l => l.emote_id === id);
            const remaining = limitInfo?.remaining ?? null;
            const effectiveLimit = limitInfo?.effective_daily_limit ?? 0;
            const extraFromShop = limitInfo?.extra_from_shop ?? 0;
            const weeklyAvailable = limitInfo?.weekly_available ?? null;
            const hitLimit = (remaining !== null && remaining <= 0) || (weeklyAvailable === false);

            const iconColor = userHasUsed ? color : theme.palette.grey[600];
            const IconComponent = Icon;
            const noAvailablePurchase = requiresPurchase && availableDislikes <= 0;
            const disabledButton = isReactDisabled || userHasUsed || comment.user_id === currentUserId || noAvailablePurchase || hitLimit;
            const usersForEmote = commentEmotes.filter((emote) => emote[key]).map((emote) => emote.user?.name || String(emote.user_id));

            // Build tooltip with remaining info
            const remainingLabel = remaining !== null && effectiveLimit > 0
                ? `Restante: ${remaining}/${effectiveLimit}${extraFromShop > 0 ? ` (${extraFromShop > 0 ? '+' + extraFromShop + ' da loja' : ''})` : ''}`
                : weeklyAvailable !== null
                    ? (weeklyAvailable ? 'Disponível esta semana' : 'Já utilizado esta semana')
                    : null;
            const tooltipTitle = (
                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, maxWidth: 250 }}>
                    <Typography component="span" variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                        {`${label} (${total})`}
                    </Typography>
                    {usersForEmote.length > 0 && (
                        <Typography component="span" variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.7rem' }}>
                            {usersForEmote.join(', ')}
                        </Typography>
                    )}
                    {remainingLabel && (
                        <Typography component="span" variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.85 }}>
                            {remainingLabel}
                        </Typography>
                    )}
                </Box>
            );

            return (
                <Tooltip key={`comment-${comment.id}-emote-${id}`} title={tooltipTitle} arrow placement="top">
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            position: 'relative',
                            borderRadius: '16px',
                            border: userHasUsed ? `1px solid ${color}` : `1px solid ${theme.palette.grey[300]}`,
                            backgroundColor: userHasUsed ? `${color}15` : 'transparent',
                            padding: '2px 8px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': !disabledButton ? {
                                backgroundColor: userHasUsed ? `${color}25` : theme.palette.grey[100],
                                transform: 'scale(1.05)',
                                boxShadow: `0 2px 8px ${userHasUsed ? `${color}40` : 'rgba(0,0,0,0.1)'}`,
                            } : {},
                            cursor: disabledButton ? 'default' : 'pointer',
                        }}
                        onClick={!disabledButton ? () => handleEmoteClick(id) : undefined}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                                size="small"
                                disabled={disabledButton}
                                sx={{
                                    padding: '0',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 1,
                                    },
                                }}
                            >
                                <IconComponent
                                    sx={{
                                        fontSize: '1.1rem',
                                        color: iconColor,
                                        transition: 'all 0.2s ease-in-out',
                                    }}
                                />
                            </IconButton>
                            {total > 0 && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: userHasUsed ? color : theme.palette.text.secondary,
                                        minWidth: '14px',
                                        textAlign: 'center',
                                        lineHeight: 1
                                    }}
                                >
                                    {total}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Tooltip>
            );
        });

        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography component="span" variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {commentUser?.name || ''}
                </Typography>
                {(user?.use_lanza_coin && commentUser?.use_lanza_coin && !!comment.id) && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            padding: '2px 6px',
                            backgroundColor: 'transparent'
                        }}
                    >
                        {badges}
                    </Box>
                )}
            </Box>
        );
    }

    let textBackgroundColor = 'none';

    if (showAssignedUsers && 'comment_task_users' in comment && !isCanceled && comment.comment_task_users) {
        if (comment.comment_task_users.find(ctu => !ctu.awared_at && ctu.user_id === user?.id)) {
            textBackgroundColor = 'rgba(255,0,0,0.3)';
        } else if (comment.comment_task_users.find(ctu => !ctu.resolved_at && ctu.user_id === user?.id)) {
            textBackgroundColor = 'rgba(255,255,0,0.3)';
        }
    }

    const isHighlighted = user?.use_lanza_coin && highlightedUserIds.has(commentUser.id);

    return (
        <>
            {(activeEmoteAnimation !== null && activeEmoteAnimation !== 1) && (
                <Portal>
                    <EmoteOverlay animDuration={activeEmoteAnimation === 4 ? DISLIKE_ANIMATION_DURATION : EMOTE_ANIMATION_DURATION}>
                        {renderEmoteAnimation(activeEmoteAnimation)}
                    </EmoteOverlay>
                </Portal>
            )}
            <Box marginBottom={3} display="flex">
                <Box
                    title={'Comentário ' + (comment.is_public ? "público" : "privado")}
                    alignItems={"center"}
                    marginRight={1}
                    style={{ backgroundColor: (comment.is_public ? "#17bb00" : "#ff3030"), padding: "0 2px" }}
                    display="flex"
                >
                    {comment.is_public ? <LockOpenIcon style={{ color: 'white', fontSize: 14 }} /> : <LockIcon style={{ color: 'white', fontSize: 14 }} />}
                </Box>
                <Box display="flex" flex={1} flexDirection="column">
                    <Box display="flex">
                        {!noUserPhoto && <Box height={60} sx={isHighlighted ? {
                            border: '2px solid #FFD700',
                            borderRadius: '6px',
                            animation: `${profileGlow} 2s ease-in-out infinite`,
                        } : undefined}>
                            <UserPhoto key={`userphoto-${commentUser.id}`} style={{ objectFit: 'contain' }} id={commentUser.id} photo={commentUser.photo} width={isHighlighted ? 51 : 55} height={isHighlighted ? 56 : 60} useLanzaCoin={commentUser.use_lanza_coin} experience={commentUser.experience} />
                        </Box>}
                        <Box flex={1} paddingLeft={1}>
                            <Box flex={1} display="flex" borderBottom={'1px solid rgb(4, 90, 142)'}>
                                <Typography variant='subtitle2' style={{ fontWeight: '700', flex: 1 }}>{getTitle()}</Typography>
                                {canGenerateTaskFromComment && <Box>
                                    <Tooltip placement="top" title={'Criar demanda a partir deste comentário'}>
                                        <IconButton
                                            size='small'
                                            color="inherit"
                                            aria-label={'Criar demanda a partir deste comentário'}
                                            onClickCapture={handleOpenCreateTaskDialog}
                                        >
                                            <PlaylistAddIcon style={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>}
                                <Typography variant='caption' textAlign="right" sx={{ display: 'flex', alignItems: 'center' }}>{created_at || ''}</Typography>
                            </Box>
                            <Box display="flex">
                                <Typography component={'div'} variant='caption' style={{ flex: 1, display: 'flex', alignItems: 'center', color: isCanceled ? 'red' : undefined }}>{getCommentSubtitle()}</Typography>
                            </Box>
                            <Collapse in={expanded} timeout="auto" style={{ width: '100%' }}>
                                <Box display="flex" flexDirection={"column"} style={{ width: '100%' }}>
                                    <Box>
                                        <Typography
                                            key={textBackgroundColor}
                                            component={'div'}
                                            variant='body2'
                                            style={{
                                                whiteSpace: 'pre-wrap',
                                                overflowWrap: 'anywhere',
                                                color: isCanceled ? 'red' : undefined,
                                                textDecoration: isCanceled ? 'line-through' : undefined,
                                                backgroundColor: textBackgroundColor
                                            }}
                                        >
                                            <div
                                                key={`comment-code-${idx}`}
                                                className="mudarCorA"
                                                dangerouslySetInnerHTML={{ __html: sanatizeComment(comment) }}
                                            />
                                        </Typography>
                                    </Box>
                                    {('prod_amount' in comment || 'prod_amount_problem' in comment) && (comment.prod_amount || comment.prod_amount_problem) ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: 1 }}>
                                            {comment.prod_amount ? <Typography variant='caption'>Quantidade produzida: {comment.prod_amount}</Typography> : null}
                                            {comment.prod_amount_problem ? <Typography variant='caption'>Quantidade com problema: {comment.prod_amount_problem}</Typography> : null}
                                        </Box>
                                    ) : null}
                                    {'attachments' in comment && comment.attachments && comment.attachments.length > 0 ? (
                                        <Box>
                                            {comment.attachments.map((at, idx) => (
                                                <AttachmentPreviewBox key={`comment-attachment-${at.id}-${idx}`} fullImageWidth={isAttachmentFullWidth} deleted={!!at.deleted_at} id={at.task_id || null} model="Task" name={at.name} systemAttachmentId={at.system_attachment_id} systemAttachmentFile={at.system_attachment?.file} />
                                            ))}
                                        </Box>
                                    ) : null}
                                    {'notify_others_users' in comment && comment.notify_others_users && comment.notify_others_users.length > 0 ? (
                                        <Box sx={{ marginTop: 1 }}>
                                            <Typography variant='caption'>
                                                Comentário enviado por e-mail a outros usuários:&nbsp;
                                                {comment.notify_others_users.map((u, idx) => (
                                                    <React.Fragment key={`notified-user-${u.id}`}>
                                                        <b>{u.name}</b>{idx < comment.notify_others_users!.length - 1 ? ', ' : ''}
                                                    </React.Fragment>
                                                ))}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                    {(!!showGeneratedTaskCreation && 'generated_task' in comment && comment.generated_task) && <Box>
                                        <Typography variant='caption' style={{ marginTop: 5 }}>
                                            Este comentário originou a demanda&nbsp;
                                            <a className='visitedBlue' href={comment.generated_task.project_id ? `#/system/task/project/${comment.generated_task.project_id}/task/${comment.generated_task.id}/general` : `#/system/task/${comment.generated_task.id}/general`} target="_blank" rel="noopener noreferrer">
                                                #{comment.generated_task.ticket_code}
                                            </a>
                                            , atualmente em <b>{comment.generated_task.ticket_status?.name}</b> com <b>{comment.generated_task.user?.name}</b> desde <b>{moment(comment.generated_task.created_at).format('DD/MM/YYYY HH:mm')}</b>.
                                        </Typography>
                                    </Box>}
                                </Box>
                            </Collapse>
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                            <ExpandMore
                                expand={expanded}
                                onClick={handleExpandClick}
                                aria-expanded={expanded}
                                aria-label="show more"
                                size='small'
                                color="inherit"
                            >
                                <ExpandMoreIcon />
                            </ExpandMore>
                        </Box>
                    </Box>

                </Box>
            </Box>
            <Dialog
                open={createTaskDialogOpen}
                onClose={handleCloseCreateTaskDialog}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, fontWeight: 700 }}>
                    <PlaylistAddIcon color="primary" fontSize="small" />
                    Selecionar responsável pela nova demanda
                </DialogTitle>
                <DialogContent sx={{ pt: 0, pb: 3 }}>
                    <Box
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }}
                    >
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1, color: 'primary.main' }}>
                            Resumo do comentário
                        </Typography>
                        <Typography variant='body2' sx={{ marginTop: 1 }} color="text.secondary">
                            {commentPreview || 'Nenhuma descrição disponível neste comentário.'}
                        </Typography>
                    </Box>
                    <Typography variant='body2' sx={{ marginBottom: 1.5 }} color="text.secondary">
                        Escolha quem receberá a nova tarefa gerada a partir deste comentário.
                    </Typography>
                    <FormControl component="fieldset" error={!selectedCreateTaskRecipient} fullWidth>
                        <RadioGroup
                            value={selectedCreateTaskRecipient ?? ''}
                            onChange={(_, value) => setSelectedCreateTaskRecipient(value as CreateTaskRecipientOption)}
                        >
                            <Stack spacing={1.5}>
                                {createTaskRecipientOptions.filter(option => !option.disabled).map(option => {
                                    const isSelected = selectedCreateTaskRecipient === option.value;
                                    const meta = optionMeta[option.value];
                                    return (
                                        <Box
                                            key={option.value}
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1.5,
                                                padding: '12px 16px',
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                backgroundColor: alpha(theme.palette.primary.main, isSelected ? 0.08 : 0.02),
                                                boxShadow: isSelected ? `0 8px 18px ${alpha(theme.palette.primary.main, 0.18)}` : 'none',
                                                cursor: option.disabled ? 'not-allowed' : 'pointer',
                                                opacity: option.disabled ? 0.5 : 1,
                                                transition: 'all 0.2s ease',
                                                '&:hover': option.disabled ? {} : {
                                                    borderColor: 'primary.main',
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.22)}`
                                                }
                                            }}
                                        >
                                            <Radio
                                                value={option.value}
                                                disabled={option.disabled}
                                                sx={{ padding: 0.5, marginTop: 0.5 }}
                                            />
                                            <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                                                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                                                    {option.label}
                                                </Typography>
                                                {meta?.helper && (
                                                    <Typography variant='body2' color="text.secondary">
                                                        {meta.helper}
                                                    </Typography>
                                                )}
                                                {meta?.chipLabel && (
                                                    <Chip
                                                        label={meta.chipLabel}
                                                        size='small'
                                                        color={meta.chipColor || 'default'}
                                                        variant={isSelected ? 'filled' : 'outlined'}
                                                        sx={{ alignSelf: 'flex-start', marginTop: 0.5 }}
                                                    />
                                                )}
                                                {option.disabledReason && (
                                                    <Typography variant='caption' color="text.secondary">
                                                        {option.disabledReason}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1 }}>
                    <Typography variant='caption' color="text.secondary">
                        A nova demanda ficará vinculada à tarefa {taskReferenceLabel} e herdará o conteúdo do comentário selecionado.
                    </Typography>
                    <Box display="flex" gap={1} justifyContent={'end'}>
                        <Button size='small' color='error' variant='contained' onClick={handleCloseCreateTaskDialog} disabled={createTaskDialogLoading}>Cancelar</Button>
                        <Button
                            onClick={handleConfirmCreateTaskDialog}
                            disabled={isConfirmCreateTaskDisabled}
                            variant="contained"
                            size='small'
                            color='primary'
                        >
                            {createTaskDialogLoading ? 'Criando...' : 'Confirmar'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}

interface TotalTimeCounterItem {
    hours: number;
    minutes: number;
}

export function TotalTimeCounterItem({ hours, minutes }: TotalTimeCounterItem) {
    const { user } = useAuthentication();
    const isAdmin = isAdminOrSuperAdmin(user);
    if (!isAdmin) return null;
    const newMinutes = minutes % 60;
    const newHours = ((minutes - newMinutes) / 60) + hours;
    return (
        <Box marginBottom={3} display="flex">
            <Box title={'Tempo total registrado'} alignItems={"center"} marginRight={1} style={{ backgroundColor: "rgb(242, 199, 30)", padding: "0 2px" }} display="flex">
                <AccessTimeIcon style={{ color: 'white', fontSize: 14 }} />
            </Box>
            <Box display="flex" flex={1} flexDirection="column">
                <Box display="flex">
                    <Box flex={1} paddingLeft={1} paddingRight={'27px'}>
                        <Box flex={1} display="flex" borderBottom={'1px solid rgb(4, 90, 142)'}>
                            <Typography variant='subtitle2' style={{ fontWeight: '700', flex: 1 }}>Tempo total registrado</Typography>
                        </Box>
                        <Box display="flex">
                            <Typography variant='caption' style={{ flex: 1 }}>{String(newHours).padStart(2, '0')}:{String(newMinutes).padStart(2, '0')}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export type HistoryItemType = Audit | CommentTask | CommentTechnicalAssistance;

export interface TicketHistoryEventEmitterType {
    newComment: CommentTask | CommentTechnicalAssistance;
}

export interface TicketHistoryProps extends WithSnackbarProps {
    type: string & 'task' | 'technicalAssistance' | 'taskPostService' | 'taskRecurrence' | 'production' | 'activities' | 'orderReceived' | 'ocurrence' | 'documentVersion';
    id?: number | null;
    comments?: (CommentTask | CommentTechnicalAssistance)[];
    ticketHistoryEventListener?: Listener<TicketHistoryEventEmitterType>;
    showTotalTimeCounter?: boolean;
    hideAudits?: boolean;
    extraWhere?: WhereCondition<Audit>;
    noUserPhoto?: boolean;
    disableCommentActions?: boolean;
    getLineTitle?: (item: HistoryItemType) => React.ReactNode;
    hideLanzaBotConclued?: boolean;
    user?: User;
    showAssignedUsers?: boolean;
    showGeneratedTaskCreation?: boolean;
    task?: Task;
    isInternal?: boolean;
}

export interface TicketHistoryState {
    historical: HistoryItemType[];
    hours: number;
    minutes: number;
}

class TicketHistory extends React.Component<TicketHistoryProps, TicketHistoryState> {

    private taskApi: TaskApi;
    private commentTaskApi: CommentTaskApi;

    constructor(props: TicketHistoryProps) {
        super(props);
        this.state = {
            historical: [],
            hours: 0,
            minutes: 0
        }
        this.addNewComment = this.addNewComment.bind(this);
        this.updateCommentAwared = this.updateCommentAwared.bind(this);
        this.updateCommentResolved = this.updateCommentResolved.bind(this);
        this.updateCommentCanceled = this.updateCommentCanceled.bind(this);
        this.filterHistorical = this.filterHistorical.bind(this);
        this.addEmote = this.addEmote.bind(this);
        this.createTaskFromComment = this.createTaskFromComment.bind(this);
        this.taskApi = new TaskApi();
        this.commentTaskApi = new CommentTaskApi();
    }

    componentDidMount(): void {
        const { ticketHistoryEventListener, hideAudits, comments } = this.props;
        if (ticketHistoryEventListener)
            ticketHistoryEventListener.on('newComment', this.addNewComment);
        if (!hideAudits) {
            this.fetchData();
        } else {
            this.setState({ historical: comments ? sortObjectArrayByProp(comments, 'created_at') : [] });
        }
    }

    componentWillUnmount(): void {
        const { ticketHistoryEventListener } = this.props;
        if (ticketHistoryEventListener)
            ticketHistoryEventListener.off('newComment', this.addNewComment);
    }

    fetchData() {
        const { id, type, comments, extraWhere } = this.props;
        if (id || extraWhere) {
            const where: WhereCondition<Audit> = extraWhere as ArrayCondition<Audit> ?? [];
            const orWhere: WhereCondition<Audit> = [];
            const extraRelations: string[] = [];
            var orderDirection: 'DESC' | 'ASC' = 'ASC';
            var noSortByProp = false;
            if (type === 'task') {
                orWhere.push(['task_id', '=', id]);
                orWhere.push(['attachment_task.task_id', '=', id]);
                orWhere.push(['task_checklist.task_id', '=', id]);
                where.push(['action', '!=', 'updatePostService']);
                where.push(['action', '!=', 'updateRecurrence']);
                where.push(['action', '!=', 'updateChecklistIsMandatory']);
                where.push(['action', '!=', 'checklistNotFilled']);
            } else if (type === 'taskPostService') {
                orWhere.push(['task_id', '=', id]);
                orWhere.push(['attachment_task.task_id', '=', id]);

                where.push(['action', 'in', ['updatePostService', 'saveAttachment', 'deleteAttachment', 'updateAttachment']]);
            } else if (type === 'taskRecurrence') {
                orWhere.push(['task_id', '=', id]);
                orWhere.push(['attachment_task.task_id', '=', id]);

                where.push(['action', 'in', ['updateRecurrence']]);
            } else if (type === 'production') {
                orWhere.push(['production_id', '=', id]);
                orWhere.push(['production_non_conformity.production_id', '=', id]);
            } else if (type === 'activities') {
                extraRelations.push('comment_task.task');
                extraRelations.push('comment_task.task.ticket_status');
                extraRelations.push('comment_task');
                extraRelations.push('comment_task.comment_task_users.user');
                extraRelations.push('comment_task.attachments');
                extraRelations.push('comment_task.attachments.system_attachment');
                extraRelations.push('comment_task.comment_task_users');
                extraRelations.push('task.ticket_status');

                orWhere.push(['comment_task_id', '!=', null]);
                orWhere.push(['task_id', '!=', null]);
                orderDirection = 'DESC';
                noSortByProp = true;
            } else if (type === 'orderReceived') {
                orWhere.push(['order_received_id', '=', id]);
                extraRelations.push('order_received.delivery_type');
                extraRelations.push('order_received.package_type');
                extraRelations.push('order_received.user');
            } else if (type === 'ocurrence') {
                orWhere.push(['ocurrence_id', '=', id]);
                orWhere.push(['ocurrence_attachment.ocurrence_id', '=', id]);
                extraRelations.push('ocurrence.user');
                extraRelations.push('ocurrence.users');
                extraRelations.push('ocurrence.final_customer');
                extraRelations.push('ocurrence.integrator');
                extraRelations.push('ocurrence.iso_process');
                extraRelations.push('ocurrence.ocurrence_type');
                extraRelations.push('ocurrence.department');
            } else if (type === 'documentVersion') {
                orWhere.push(['document_version_id', '=', id]);
            } else {
                orWhere.push(['technical_assistance_id', '=', id]);
                orWhere.push(['attachment_technical_assistance.technical_assistance_id', '=', id]);
            }
            apiAudit.getList({
                where, orWhere, relations: [
                    'audit_user', 'task', 'attachment_task', 'attachment_task.system_attachment', 'attachment_technical_assistance', 'technical_assistance',
                    'production', 'production_non_conformity', 'task_checklist', 'ocurrence_attachment',
                    'order_received', 'ocurrence', 'document_version', ...(extraRelations ?? [])
                ], order: { created_at: orderDirection }
            })
                .then((result) => {
                    var audits = result;

                    if (type === 'task') {
                        audits = audits.filter((a) => !['saveAttachment', 'deleteAttachment', 'updateAttachment'].includes(a.action) || a.attachment_task?.type !== AttachmentTaskType.PostService);
                    } else if (type === 'taskPostService') {
                        audits = audits.filter((a) => !['saveAttachment', 'deleteAttachment', 'updateAttachment'].includes(a.action) || a.attachment_task?.type === AttachmentTaskType.PostService);
                    }

                    var hours = 0;
                    var minutes = 0;

                    comments?.forEach((c) => {
                        if ('time_counter' in c && c.time_counter) {
                            hours += c.time_counter.hours;
                            minutes += c.time_counter.minutes;
                        }
                    })

                    const list: HistoryItemType[] = [...(comments ?? []), ...audits];

                    this.setState({
                        historical: noSortByProp ? list : sortObjectArrayByProp(list, 'created_at'),
                        hours,
                        minutes
                    });
                });
        }
    }

    addNewComment(newComment: (CommentTask | CommentTechnicalAssistance)) {
        const { historical, hours, minutes } = this.state;
        this.setState({
            historical: [...historical, newComment],
            hours: hours + ('time_counter' in newComment && newComment.time_counter ? newComment.time_counter.hours : 0),
            minutes: minutes + ('time_counter' in newComment && newComment.time_counter ? newComment.time_counter.minutes : 0)
        });
    }

    async updateCommentResolved(commentTaskUserId: number) {
        const { historical } = this.state;
        const idxComment = historical.findIndex(h => 'comment_task_users' in h && h.comment_task_users && h.comment_task_users.findIndex(hh => hh.id === commentTaskUserId) >= 0);
        if (idxComment >= 0) {
            const updatedCommentTaskUser = await this.taskApi.resolveComment(commentTaskUserId);
            const newHistorical = objectClone(historical);
            const updatedComment = newHistorical[idxComment];
            if ('comment_task_users' in updatedComment && updatedComment.comment_task_users) {
                updatedComment.comment_task_users = updatedComment.comment_task_users.map(ctu => ctu.id === commentTaskUserId ? updatedCommentTaskUser : ctu);
            }
            this.setState({ historical: newHistorical });
            eventEmitter.emit('commentResolved', updatedComment);
        }
    }

    async updateCommentAwared(commentTaskUserId: number) {
        const { historical } = this.state;
        const idxComment = historical.findIndex(h => 'comment_task_users' in h && h.comment_task_users && h.comment_task_users.findIndex(hh => hh.id === commentTaskUserId) >= 0);
        if (idxComment >= 0) {
            const updatedCommentTaskUser = await this.taskApi.awaredComment(commentTaskUserId);
            const newHistorical = objectClone(historical);
            const updatedComment = newHistorical[idxComment];
            if ('comment_task_users' in updatedComment && updatedComment.comment_task_users) {
                updatedComment.comment_task_users = updatedComment.comment_task_users.map(ctu => ctu.id === commentTaskUserId ? updatedCommentTaskUser : ctu);
            }
            this.setState({ historical: newHistorical });
            eventEmitter.emit('commentAwared', updatedComment);
        }
    }

    async updateCommentCanceled(commentTaskId: number) {
        const { historical } = this.state;
        const idxComment = historical.findIndex(h => h.id === commentTaskId && 'is_public' in h);
        if (idxComment >= 0) {
            const updatedComment = await this.taskApi.cancelComment(commentTaskId);
            const newHistorical = objectClone(historical);
            newHistorical[idxComment] = updatedComment;
            this.setState({ historical: newHistorical });
        }
    }

    async addEmote(commentId: number, emoteId: number) {
        const { enqueueSnackbar } = this.props;
        try {
            await this.commentTaskApi.addEmote(commentId, emoteId);
            const commentUpdated = await this.commentTaskApi.getById(commentId, { relations: ['emotes', 'emotes.user'] });
            const { historical } = this.state;
            const newHistorical = objectClone(historical);
            const idxComment = newHistorical.findIndex(h => h.id === commentId && 'is_public' in h);
            if (idxComment >= 0) {
                const updatedComment = newHistorical[idxComment];
                newHistorical[idxComment] = { ...updatedComment, emotes: commentUpdated.emotes };
                this.setState({ historical: newHistorical });
            }
            return true;
        } catch (error: any) {
            enqueueSnackbar(error?.message || '', { variant: 'error' });
            return false;
        }
    }

    filterHistorical(item: HistoryItemType) {
        const { hideLanzaBotConclued, user } = this.props;
        if ('controller' in item) {
            if (item.action === 'saveAttachment' && item.attachment_task?.comment_task_id) {
                return false;
            };
        } else if ('comment_task_users' in item) {
            {/* item?.comments?.filter(c => c.type === CommentTaskType.PreService && (showLanzaBotConclued || c.user?.name !== 'LanzaBot' || (c.comment_task_users && c.comment_task_users.filter(cc => cc.resolved_at).length === 0))) || [] */ }
            let solvedComments = item.comment_task_users?.filter(cc => cc.resolved_at) || [];
            if (hideLanzaBotConclued && item.user?.name === 'LanzaBot' && item.comment_task_users && (item.comment_task_users.filter(cc => cc.resolved_at && cc.user_id === user?.id).length > 0 || (solvedComments.length === item.comment_task_users.length && item.comment_task_users.length > 0))) {
                return false;
            }
        }
        return true;
    }

    async createTaskFromComment(comment: CommentTask, assignedUserId: number) {
        const { historical } = this.state;
        const { task, user, isInternal, enqueueSnackbar } = this.props;
        if (!task || !user) return;
        if (!assignedUserId) {
            enqueueSnackbar('Selecione um responsável válido para a nova demanda.', { variant: 'warning' });
            return;
        }
        try {
            const insertedNewTask = await this.taskApi.insert({
                name: `Demanda gerada a partir da tarefa #${task.ticket_code}`,
                description: comment.comment,
                ticket_status_id: FixedIds.ticketStatusFila,
                created_by_user_id: user.id,
                ticket_type_id: FixedIds.ticketTypeProposalForImprovementOfExistingFunctionality,
                user_id: assignedUserId,
                project_id: isInternal ? task?.project_id : null,
                final_customer_id: FixedIds.finalCustomerCommboxId
            });
            if (!insertedNewTask) throw new Error('Erro ao salvar chamado');
            const newTask = await this.taskApi.getById(insertedNewTask.id, { relations: ['ticket_status', 'user'] });
            await this.taskApi.cloneCommentAttachmentsToTask(comment.id, newTask.id);
            await this.taskApi.updateReferences(newTask.id, [task.id]);
            await this.taskApi.linkCommentToTask(comment.id, newTask.id);
            await this.taskApi.sendEmail(newTask.id, TaskSendEmailType.StatusChange, user.id);
            await this.taskApi.sendEmail(newTask.id, TaskSendEmailType.RegisterAssignedTo);
            const newHistorical = objectClone(historical);
            const idxComment = newHistorical.findIndex(h => h.id === comment.id && 'is_public' in h);
            if (idxComment >= 0) {
                const updatedComment = newHistorical[idxComment];
                newHistorical[idxComment] = { ...updatedComment, generated_task: newTask, generated_task_id: newTask.id };
                this.setState({ historical: newHistorical });
            }
            enqueueSnackbar('Demanda criada com sucesso!', { variant: 'success' });
        } catch (error) {
            console.error('Erro ao criar tarefa a partir do comentário:', error);
            enqueueSnackbar('Erro ao criar demanda a partir do comentário.', { variant: 'error' });
        }
    }

    render(): React.ReactNode {
        const { showTotalTimeCounter, noUserPhoto, disableCommentActions, getLineTitle, showAssignedUsers, user, showGeneratedTaskCreation, task } = this.props;
        const { historical, hours, minutes } = this.state;
        return (
            <>
                {showTotalTimeCounter ? <TotalTimeCounterItem hours={hours} minutes={minutes} /> : null}
                {historical.filter(this.filterHistorical).map((item, key) => {
                    var object: Audit | CommentTask | CommentTechnicalAssistance | CommentOcurrence | CommentDocumentVersion = item;
                    if ('controller' in object && (object.comment_task_id || object.comment_technical_assistance_id || object.comment_ocurrence_id || object.comment_document_version_id)) {
                        if (object.comment_task_id && object.comment_task) {
                            object = object.comment_task;
                        } else if (object.comment_technical_assistance_id && object.comment_technical_assistance) {
                            object = object.comment_technical_assistance;
                        } else if (object.comment_ocurrence_id && object.comment_ocurrence) {
                            object = object.comment_ocurrence;
                        } else if (object.comment_document_version_id && object.comment_document_version) {
                            object = object.comment_document_version;
                        }
                    }

                    if ('controller' in object) {
                        return <AuditItem
                            key={`${key}-audit`}
                            getLineTitle={getLineTitle}
                            noUserPhoto={noUserPhoto}
                            audit={object}
                            user={user}
                        />;
                    } else if ('is_public' in object) {
                        return <CommentItem
                            key={`${key}-comment`}
                            comment={object}
                            updateCommentAwared={this.updateCommentAwared}
                            updateCommentResolved={this.updateCommentResolved}
                            updateCommentCanceled={this.updateCommentCanceled}
                            idx={key}
                            noUserPhoto={noUserPhoto}
                            disableCommentActions={disableCommentActions}
                            getLineTitle={getLineTitle}
                            showAssignedUsers={showAssignedUsers}
                            addEmote={this.addEmote}
                            showGeneratedTaskCreation={showGeneratedTaskCreation}
                            createTaskFromComment={this.createTaskFromComment}
                            task={task}
                        />;
                    }
                })}
            </>
        );
    }
}

export default withSnackbar(TicketHistory);